import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import Form from "@/components/Form";
import { List } from "@/components/list/List";
import SaveButton from "@/components/SaveButton";
import Spinner from "@/components/Spinner";
import Title from "@/components/Title";
import {
  Module,
  PATH_PAYROLL,
  PATH_PAYROLL_AGGREGATION_DETAIL,
} from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import useQueryData from "@/hooks/useQueryData";
import { useSyncFormWithQueryData } from "@/hooks/useSyncFormWithQueryData";
import { usePayrollBreadcrumbs } from "@/modules/payrolls/hooks/usePayrollBreadcrumbs";
import { usePayrollParams } from "@/modules/payrolls/hooks/usePayrollParams";
import { payrollAggregate } from "@/schemas/payrollAggregate";
import { FetchListRequest, SuccessFetchListResponse } from "@/services/api";
import { PayrollsService } from "@/services/payrolls";
import {
  PayrollAggregate,
  UpdatePayrollAggregate,
} from "@/types/payrollAggregate";
import { zodResolver } from "@hookform/resolvers/zod";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { memo, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath, Navigate } from "react-router-dom";
import { useFormBaseline } from "@/hooks/useFormBaseline";
import { useFormDefaultValuesFromQuery } from "@/hooks/useFormDefaultValuesFromQuery";
import { usePayrollAggregateColumnOverrides } from "../hooks/usePayrollAggregateColumnOverrides";
import { usePayrollPermissions } from "../hooks/usePayrollPermissions";
import { useUpdatePayrollAggregateMutation } from "../hooks/useUpdatePayrollAggregateMutation";
import { initialColumnVisibility } from "../initialColumnVisibility";
import { writePayrollAggregateFormSchema } from "../schema";
import { WritePayrollAggregateForm } from "../types";
import { convertFormToUpdatePayrollDetails } from "../utils/convertFormToUpdatePayrollDetails";
import { PayrollAggregationRecordView } from "./PayrollAggregationRecordView";
import { PayrollCloseButton } from "./PayrollCloseButton";
import { PayrollCorrectionButton } from "./PayrollCorrectionButton";
import { PayrollSplitButton } from "./PayrollSplitButton";
import { PayrollSendContestConfirmationButton } from "./PayrollSendContestConfirmationButton";

const defaultValues: WritePayrollAggregateForm = {
  entries: {},
};

export const PayrollAggregationDirectory = memo(() => {
  const { payrollId } = usePayrollParams();
  const { t } = useTranslation();
  const permissions = usePayrollPermissions();

  const name = Module.Payrolls;

  const fetchList = useCallback(
    (params: FetchListRequest<PayrollAggregate>) =>
      PayrollsService.getAggregationList(payrollId, params),
    [payrollId],
  );

  const pathBuilder = useCallback(
    (row: PayrollAggregate) =>
      generatePath(PATH_PAYROLL_AGGREGATION_DETAIL, {
        payrollId,
        entryId: row.id,
      }),
    [payrollId],
  );

  const breadcrumbs = usePayrollBreadcrumbs([
    {
      label: t("payrolls.views.aggregate.title"),
    },
  ]);

  const options = useCreateDataTableOptions<PayrollAggregate>(
    `${name}-aggregation`,
  );

  const queryKey = useDynamicQueryParams(
    [name, payrollId, "aggregation"],
    options,
  );

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      fetchList({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [fetchList, options.filters, options.sorting],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<PayrollAggregate>) =>
      lastPage.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query }), [query]);

  const columnOverrides = usePayrollAggregateColumnOverrides();
  const mutation = useUpdatePayrollAggregateMutation();

  const data = useQueryData(query);
  const defaultValuesFromQuery = useFormDefaultValuesFromQuery<
    PayrollAggregate,
    "entries",
    WritePayrollAggregateForm
  >(data);

  const form = useForm<WritePayrollAggregateForm>({
    resolver: zodResolver(writePayrollAggregateFormSchema),
    defaultValues,
    disabled: !permissions.canUpdateAggregate || mutation.isPending,
  });

  const { baseline, updateBaseline } = useFormBaseline(
    form,
    defaultValuesFromQuery,
  );

  const handleSubmit = useCallback(
    async (values: WritePayrollAggregateForm) => {
      const dirtyFields = form.formState.dirtyFields;
      const payload = convertFormToUpdatePayrollDetails<UpdatePayrollAggregate>(
        values,
        dirtyFields,
      );
      await mutation.mutateAsync(payload);
      updateBaseline();
    },
    [form.formState.dirtyFields, mutation, updateBaseline],
  );

  useSyncFormWithQueryData(data, form);

  if (permissions.isLoading) {
    return <Spinner className="w-full h-full" />;
  }

  if (!permissions.canAccessAggregation) {
    return (
      <Navigate
        to={generatePath(PATH_PAYROLL, {
          payrollId,
        })}
        replace
      />
    );
  }

  return (
    <List
      schema={payrollAggregate}
      name={name}
      pathBuilder={pathBuilder}
      query={query}
      options={options}
      columnOverrides={columnOverrides}
      tableFeatures={{
        editionHighlight: { baseline },
        initialColumnVisibility,
        exportSettings: {
          moduleName: Module.Payrolls,
          queryParams: {
            payroll_stage: "aggregation",
            payroll_id: payrollId,
          },
        },
      }}
    >
      <List.Header
        breadcrumbLabel={`${name}.title.multiple`}
        breadcrumbs={breadcrumbs}
        actions={[
          permissions.canSplit && <PayrollSplitButton />,
          permissions.canCloseAggregate && <PayrollCloseButton />,
          permissions.canCorrect && <PayrollCorrectionButton />,
          <PayrollSendContestConfirmationButton />,
        ]}
      />
      <Title>{t("payrolls.views.aggregate.title")}</Title>
      <PayrollAggregationRecordView />
      <List.Menubar>
        <List.Menubar.Tools />
        <List.Menubar.Columns />
        <List.Menubar.Reset />
      </List.Menubar>
      <DataTableQueryContextProvider value={queryValue}>
        <Form form={form} handleSubmit={handleSubmit}>
          <List.Content>
            <List.Status>
              <List.DirtyRowsStatus />
            </List.Status>
          </List.Content>
          <footer className="flex py-2 justify-end gap-6">
            <SaveButton />
          </footer>
        </Form>
      </DataTableQueryContextProvider>
    </List>
  );
});
